import { Button } from "components/DesignSystem/Button/Button";
import { PayForUnpaidTask } from "components/PayForUnpaidTask";
import { CLICKED_FILING_ACTION_CTA_ON_FILINGS } from "constants/analyticsEvents";
import { DONT_NEED, FILED_EXTERNALLY } from "constants/cartItemDeletionReasons";
import {
  ADD_DETAILS_CTA,
  FilingActionCtaLabel,
  GO_TO_CART_CTA,
  PAY_CTA,
  VIEW_ACTIONS_CTA,
} from "constants/filingActionCta";
import { CART_STATUS, TASK_CATEGORY_TYPES, TASK_SOURCE } from "constants/task";
import { FORM_FILING, PAYMENT } from "constants/taskStates";
import { useAnalytics } from "hooks/useAnalytics";
import React from "react";
import { TaskDeadlineOverviewItem } from "types/Models/task";
import { categorizeFilingState } from "utils/categorizeFilingState";
import { openLink } from "utils/openLink";
import { LaunchFiling } from "./LaunchFiling";
import { RedirectFromTaskToCart } from "./RedirectFromTaskToCart";

const NO_ACTION = "-";

export const FilingActionCta = ({
  item,
  isTaskCompleted,
  onOpenActions,
  hideEmpty = false,
}: {
  item: TaskDeadlineOverviewItem;
  isTaskCompleted: boolean;
  onOpenActions: (taskId: string) => void;
  hideEmpty?: boolean;
}) => {
  const noAction = hideEmpty ? null : <>{NO_ACTION}</>;

  const { trackEvent } = useAnalytics();

  const trackCtaClick = (ctaLabel: FilingActionCtaLabel) =>
    trackEvent(CLICKED_FILING_ACTION_CTA_ON_FILINGS, {
      filing_name: item.task_title,
      cta_label: ctaLabel,
      is_launched: Boolean(item.task_uuid),
    });

  const handleViewActionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackCtaClick(VIEW_ACTIONS_CTA);
    onOpenActions(item.task_uuid || "");
  };

  const handleAddDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackCtaClick(ADD_DETAILS_CTA);
    openLink(`/tax/filings/${item.task_uuid}`);
  };

  const handleGoToCartClick = () => trackCtaClick(GO_TO_CART_CTA);

  const handlePayClick = () => trackCtaClick(PAY_CTA);

  const isAddedToCart =
    item.source === TASK_SOURCE.CART &&
    item.cart_status === CART_STATUS.IN_CART;

  if (
    item.is_archived ||
    item.delete_reason === DONT_NEED ||
    item.delete_reason === FILED_EXTERNALLY
  ) {
    return noAction;
  }

  if (isAddedToCart) {
    return (
      <RedirectFromTaskToCart
        entityId={item.entity?.uuid}
        onClick={handleGoToCartClick}
      />
    );
  }

  if (categorizeFilingState(item.current_state.type) === FORM_FILING) {
    return (
      <Button size="xsmall" onClick={handleAddDetailsClick}>
        {ADD_DETAILS_CTA}
      </Button>
    );
  }

  if (
    !item.task_uuid &&
    item.base_task_template.uuid &&
    item.product_content_type_id
  ) {
    return <LaunchFiling item={item} onPayClick={handlePayClick} />;
  }

  const isPaymentAmountAvailable =
    typeof item.payment_amount === "number" && !isNaN(item.payment_amount);

  const skipBooksPayment =
    item.task_category_type !== TASK_CATEGORY_TYPES.BOOKS;

  const isTaskInPaymentState = item.current_state.type === PAYMENT;

  const isUnpaidTask =
    isPaymentAmountAvailable &&
    item.payment_status !== "PAID" &&
    skipBooksPayment &&
    isTaskInPaymentState;

  if (isUnpaidTask) {
    return (
      <PayForUnpaidTask
        task={item}
        finalPrice={item.payment_amount}
        onPayClick={handlePayClick}
      />
    );
  }

  if (item.is_action_pending && !isTaskCompleted) {
    return (
      <Button size="xsmall" onClick={handleViewActionsClick}>
        {VIEW_ACTIONS_CTA}
      </Button>
    );
  }

  return noAction;
};
